import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Grid } from '@material-ui/core';
import clx from 'classnames';
import { observer } from 'mobx-react';
import { useMutation } from '@apollo/client';
import { documentTypes } from '@ourbranch/lookups';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import { useStore } from 'core/store';
import { Label } from 'core';
import Button from 'core/components/button/button';
import { FormField } from 'core/components/form';
import { Card } from 'core/components/card';
import Divider from 'core/components/divider';
import emailIcon from 'core/assets/svg/email.svg';
import copyIcon from 'core/assets/svg/copy.svg';
import spinnerIcon from 'core/assets/svg/ic-spinner.svg';
import { useToast } from 'core/components/toast';
import { GENERATE_MEMBER_FORM } from 'customer/components/policy/policy.queries';
import { track } from 'core/helpers/analytics';
import useSession from 'core/hooks/use-session';

import useStyles from './changes-for-approval-section.styles';

const LoadingButtonContent = ({ classes }) => {
  return (
    <>
      <img height={24} width={24} src={spinnerIcon} alt="loading-spinner" className={classes.loadingSpinner} />{' '}
      Generating form...
    </>
  );
};

const newPIPFormRequiredValidationSchema = () =>
  Yup.object().shape({
    newPIPFormRecipientEmail: Yup.string().email('Invalid email address').required('Email is required')
  });

const NewPIPFormRequiredBlock = ({ isProcessingView = false, previewId, onLoadingStateChange }) => {
  const clickedButtonRef = useRef(null);
  const classes = useStyles();
  const toast = useToast();
  const {
    account: {
      email,
      policies: {
        policy: {
          policy: { id: policyId }
        }
      }
    }
  } = useStore();
  const { isAgency, user } = useSession();

  const [generateMemberForm, { loading: generateMemberFormLoading }] = useMutation(GENERATE_MEMBER_FORM);

  useEffect(() => {
    if (onLoadingStateChange) {
      onLoadingStateChange(generateMemberFormLoading);
    }
  }, [generateMemberFormLoading, onLoadingStateChange]);

  const onGenerateForm = async (values) => {
    const { data, error } = await generateMemberForm({
      variables: {
        documentType: documentTypes.PIP_SELECTION_FORM,
        memberEmail: values.shouldSendEmail ? values.newPIPFormRecipientEmail : undefined,
        policyId: policyId,
        previewId: previewId
      }
    });

    if (error) {
      toast.notify({
        type: 'error',
        message: 'The document and link failed to generate. Please try again.'
      });
      return;
    }

    track('Change request - Generate new PIP form', {
      isAgency,
      username: user.username
    });

    const documentLink = data.generateMemberForm.documentLink;

    if (!values.shouldSendEmail) {
      try {
        // Requires https to work
        await navigator.clipboard.writeText(documentLink);
        toast.notify({
          type: 'success',
          message: 'The PIP Selection/Rejection form link was copied to your clipboard.'
        });
      } catch (error) {
        console.error('Failed to copy to clipboard:', error);
        toast.notify({
          type: 'error',
          message: 'Failed to copy link to clipboard. Please try again.'
        });
      }
    } else {
      toast.notify({
        type: 'success',
        message: `The PIP Selection/Rejection form has been sent to ${values.newPIPFormRecipientEmail} for their signature.`
      });
    }
  };

  return (
    <Formik
      initialValues={{ newPIPFormRecipientEmail: email, shouldSendEmail: true }}
      // Show error toast when submitting form with invalid or missingemail
      validationSchema={newPIPFormRequiredValidationSchema}
      onSubmit={onGenerateForm}
    >
      {({ handleSubmit, setFieldValue }) => {
        return (
          <Form>
            <Card type="primary" className={classes.pipChangeContainer}>
              <Label type="darkGreenSmallBold">New PIP Selection/Rejection form required</Label>
              <p className={classes.pipChangeDescription}>
                {isProcessingView
                  ? 'This change will require a new PIP Selection/Rejection form signed by the member. If one has not been already provided, please send a new form via one of the following methods:'
                  : 'The requested changes require an updated PIP Selection/Rejection form to be signed by the member before these changes can be applied. Please use one of the methods below to send a link to our online signing experience to the member.'}
              </p>
              <Grid container spacing={2} alignItems="center" className={classes.emailContainer}>
                <Grid item xs={7}>
                  <FormField
                    mode="light"
                    label="Email"
                    type="string"
                    id="newPIPFormRecipientEmail"
                    name="newPIPFormRecipientEmail"
                    ignoreGlobalDisabledState
                  />
                </Grid>
                <Grid item xs={5}>
                  <Button
                    className={classes.sendFormActionButton}
                    onClick={() => {
                      clickedButtonRef.current = 'sendEmail';
                      setFieldValue('shouldSendEmail', true);
                      handleSubmit();
                    }}
                    disabled={generateMemberFormLoading}
                  >
                    {generateMemberFormLoading && clickedButtonRef.current === 'sendEmail' ? (
                      <LoadingButtonContent classes={classes} />
                    ) : (
                      <>
                        <img height={24} width={24} src={emailIcon} alt="email" /> Generate form and send
                      </>
                    )}
                  </Button>
                </Grid>
              </Grid>
              <Divider label="Or" />
              <Button
                className={clx(classes.sendFormActionButton, classes.generateFormAndCopyActionButton)}
                onClick={() => {
                  clickedButtonRef.current = 'copyLink';
                  setFieldValue('shouldSendEmail', false);
                  handleSubmit();
                }}
                disabled={generateMemberFormLoading}
              >
                {generateMemberFormLoading && clickedButtonRef.current === 'copyLink' ? (
                  <LoadingButtonContent classes={classes} />
                ) : (
                  <>
                    <img height={24} width={24} src={copyIcon} alt="copy" /> Generate form and copy link to clipboard
                  </>
                )}
              </Button>
            </Card>
          </Form>
        );
      }}
    </Formik>
  );
};

NewPIPFormRequiredBlock.propTypes = {
  isProcessingView: PropTypes.bool.isRequired,
  previewId: PropTypes.string.isRequired,
  onLoadingStateChange: PropTypes.func
};

export default observer(NewPIPFormRequiredBlock);
